import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, Sparkles } from 'lucide-react';
import { Header } from '@/components/Header';
import { ResourceFilterSidebar } from '@/components/ResourceFilterSidebar';
import { ResourceCard } from '@/components/ResourceCard';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useResources, type ResourceFilters } from '@/hooks/useResources';
import { AIFilter } from '@/components/AIFilter';
import { AIResults } from '@/components/AIResults';

const sortOptions = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'title', label: 'Title A-Z' },
  { value: 'updated', label: 'Recently Updated' },
];

export default function Resources() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showAIFilter, setShowAIFilter] = useState(false);
  const [aiResults, setAIResults] = useState<any>(null);
  
  const [filters, setFilters] = useState<ResourceFilters>({
    contentTypes: searchParams.get('contentType')?.split(',').filter(Boolean) || [],
    topics: searchParams.get('topic')?.split(',').filter(Boolean) || [],
    useCases: searchParams.get('useCase')?.split(',').filter(Boolean) || [],
  });

  const { data: resources = [], isLoading, error, refetch } = useResources(searchQuery, filters, sortBy);

  // Sync URL params
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) params.set('q', searchQuery);
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    if (filters.contentTypes.length) params.set('contentType', filters.contentTypes.join(','));
    if (filters.topics.length) params.set('topic', filters.topics.join(','));
    if (filters.useCases.length) params.set('useCase', filters.useCases.join(','));

    setSearchParams(params, { replace: true });
  }, [searchQuery, sortBy, filters, setSearchParams]);

  const activeFilterCount = 
    filters.contentTypes.length + 
    filters.topics.length + 
    filters.useCases.length;

  const clearAllFilters = () => {
    setFilters({
      contentTypes: [],
      topics: [],
      useCases: [],
    });
    setSearchQuery('');
  };

  const removeFilter = (type: keyof ResourceFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [type]: (prev[type] as string[]).filter(item => item !== value)
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Search resources..."
      />

      <div className="flex">
        <ResourceFilterSidebar
          filters={filters}
          onFiltersChange={setFilters}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
        />

        <main className="flex-1 min-w-0">
          <div className="container mx-auto px-4 py-8">
            {/* Page title */}
            <div className="mb-8">
              <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">
                Design Resources
              </h1>
              <p className="text-text-secondary text-lg">
                Discover curated resources to level up your design skills
              </p>
            </div>

            {/* AI Filter */}
            {!showAIFilter && !aiResults && (
              <div className="mb-6">
                <Button
                  onClick={() => setShowAIFilter(true)}
                  variant="outline"
                  className="border-accent/30 hover:border-accent hover:bg-accent-light/10 text-accent hover:text-accent hover:scale-105 transition-all duration-200"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Try AI Filter
                  <Badge variant="secondary" className="ml-2 bg-accent-light text-accent text-xs">
                    Beta
                  </Badge>
                </Button>
              </div>
            )}

            {showAIFilter && !aiResults && (
              <AIFilter
                type="resources"
                data={resources} // Pass current resources data
                onResults={(results) => {
                  setAIResults(results);
                  setShowAIFilter(false);
                }}
                onClose={() => setShowAIFilter(false)}
              />
            )}

            {/* Remove the separate AI Results section */}
            {/* AI Results were moved to the main results section */}

            {/* Controls bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFiltersOpen(true)}
                  className="lg:hidden"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-accent-light text-accent">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>

                <div className="text-sm text-text-secondary">
                  {isLoading ? (
                    "Loading resources..."
                  ) : (
                    <>
                      <span className="font-medium text-text-primary">{resources.length}</span> resources found
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* View mode toggle */}
                <div className="flex items-center border border-border rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8 px-3"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="h-8 px-3"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active filters */}
            {(activeFilterCount > 0 || searchQuery) && (
              <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-surface rounded-lg">
                <span className="text-sm text-text-secondary">Active filters:</span>
                
                {searchQuery && (
                  <Badge variant="secondary" className="bg-accent-light text-accent">
                    Search: "{searchQuery}"
                    <button
                      onClick={() => setSearchQuery('')}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                )}

                {filters.contentTypes.map(type => (
                  <Badge key={type} variant="secondary">
                    {type}
                    <button
                      onClick={() => removeFilter('contentTypes', type)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.topics.map(topic => (
                  <Badge key={topic} variant="secondary">
                    {topic}
                    <button
                      onClick={() => removeFilter('topics', topic)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.useCases.map(useCase => (
                  <Badge key={useCase} variant="secondary">
                    {useCase}
                    <button
                      onClick={() => removeFilter('useCases', useCase)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-text-tertiary hover:text-error text-sm"
                >
                  Clear all
                </Button>
              </div>
            )}

            {/* Results */}
            {error ? (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                    <Filter className="h-8 w-8 text-text-tertiary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                    Error loading resources
                  </h3>
                  <p className="text-text-secondary mb-6">
                    {error.message}
                  </p>
                  <Button onClick={() => refetch()} variant="outline">
                    Try again
                  </Button>
                </div>
              </div>
            ) : aiResults ? (
              /* Show only AI results when AI search is active */
              <AIResults
                results={aiResults.results}
                appliedFilters={aiResults.appliedFilters}
                intent={aiResults.intent}
                totalFound={aiResults.totalFound}
                type="resources"
                onClose={() => setAIResults(null)}
              />
            ) : isLoading ? (
              <div className="text-center py-16">
                <div className="text-text-secondary">Loading resources...</div>
              </div>
            ) : resources.length > 0 ? (
              <div className={
                viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"
                  : "space-y-4"
              }>
                {resources.map((resource, index) => (
                  <ResourceCard key={resource["Resource Name"] + index} resource={resource} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                    <Filter className="h-8 w-8 text-text-tertiary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                    No resources found
                  </h3>
                  <p className="text-text-secondary mb-6">
                    No matches for your current search and filters. Try clearing some filters or broadening your search.
                  </p>
                  <Button onClick={clearAllFilters} variant="outline">
                    Clear all filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}